import React, { useState, useMemo } from "react";
import Modal from "../common/Modal/Modal";
import "./OpeningHours.scss";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import * as actions from "../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import i18n from "../../i18n";
import Loading from "../Loading/DataLoading";
import EditableDaySchedule, {
  ensureTwoSlots,
  normalizeTimeValue,
  clampToHalfHour,
  validateDay,
  buildHalfHourOptions,
  buildCloseOptions,
  formatTimeLabel,
  getTimeInputLocale,
  getUses12HourFormat,
} from "./EditableDaySchedule";

const OpeningHoursConfirmationModal = ({ editHours = false, standalone = false }) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(0);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const openingHours = useSelector((state) => state.openingHours.openingHours);
  const storages = useSelector((state) => state.storage.storages);
  const openingHoursError = useSelector((state) => state.openingHours.error);
  const storagesError = useSelector((state) => state.storage.error);
  const { language } = i18n;
  const [isOpen, setIsOpen] = useState(true);
  const [storageSchedules, setStorageSchedules] = useState({});
  const [originalSchedules, setOriginalSchedules] = useState({});
  const [originalPayloads, setOriginalPayloads] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  // Use storages directly as locations
  const locations = storages || [];

  const dayKeys = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const timeInputLocale = useMemo(
    () => getTimeInputLocale(language),
    [language],
  );

  const uses12HourFormat = useMemo(
    () => getUses12HourFormat(timeInputLocale),
    [timeInputLocale],
  );

  const halfHourOptions = useMemo(() => buildHalfHourOptions(), []);
  const closeHalfHourOptions = useMemo(() => buildCloseOptions(), []);

  const buildEditableSchedules = (schedules = {}) => {
    const result = {};
    Object.keys(schedules).forEach((storageId) => {
      result[storageId] = dayKeys.map((dayKey, idx) => {
        const day = schedules[storageId][idx] || {
          dayKey,
          isClosed: true,
          slots: [],
        };
        return {
          dayKey,
          isClosed:
            day.isClosed ||
            !day.slots ||
            (Array.isArray(day.slots) && day.slots.length === 0),
          showSecondSlot: (day.slots || []).length > 1,
          slots: ensureTwoSlots(day.slots || []),
        };
      });
    });
    return result;
  };

  const cloneSchedules = (schedules) =>
    JSON.parse(JSON.stringify(schedules || {}));

  const buildTimesPayload = (scheduleDays = []) => {
    const payload = {};

    dayKeys.forEach((_, idx) => {
      const day = scheduleDays[idx];
      if (!day || day.isClosed) {
        payload[idx.toString()] = [];
        return;
      }

      const slots = (day.slots || []).filter(
        (slot) => slot.open && slot.close,
      );

      const completeSlots = slots.filter((slot, slotIdx) => {
        if (slotIdx === 1 && !day.showSecondSlot) return false;
        return slot.open && slot.close;
      });

      payload[idx.toString()] = completeSlots.map((slot) => [
        normalizeTimeValue(slot.open),
        normalizeTimeValue(slot.close),
      ]);
    });

    return payload;
  };

  const buildPayloadMap = (schedules = {}) => {
    const payloads = {};
    Object.keys(schedules).forEach((storageId) => {
      payloads[storageId] = buildTimesPayload(schedules[storageId]);
    });
    return payloads;
  };

  const validateSchedule = (scheduleDays = []) => {
    return scheduleDays.every((day) => validateDay(day).valid);
  };

  // Transform API response to schedule format
  const transformOpeningHoursToSchedule = (openingHoursData) => {
    return dayKeys.map((dayKey, index) => {
      // API uses 0=Monday, 1=Tuesday, etc.
      const daySlots = openingHoursData[index.toString()];

      if (!daySlots || daySlots.length === 0) {
        return {
          dayKey,
          isClosed: true,
          slots: [],
        };
      }

      return {
        dayKey,
        isClosed: false,
        slots: daySlots.map((slot) => ({
          open: normalizeTimeValue(slot[0]),
          close: normalizeTimeValue(slot[1]),
        })),
      };
    });
  };

  // Memoize filtered opening hours that need review and their schedules
  const needsReviewData = useMemo(() => {
    if (!openingHours || !Array.isArray(openingHours)) {
      return { storageIds: [], schedules: {} };
    }

    const filtered = standalone
      ? openingHours
      : openingHours.filter((oh) => oh.needsReview);
    console.log("Opening hours that need review:", filtered);

    const schedules = {};

    filtered.forEach((storage) => {
      const storageId = storage.storageId;
      schedules[storageId] = transformOpeningHoursToSchedule(
        storage.openingHours,
      );
    });

    return {
      storageIds: filtered.map((oh) => oh.storageId),
      schedules: schedules,
    };
  }, [openingHours]);

  useEffect(() => {
    if (!openingHours) {
      console.log("Fetching opening hours...");
      dispatch(actions.initOpeningHours());
      !storages && dispatch(actions.initStorages(language));
    }
  }, [dispatch, openingHours, storages, language]);

  useEffect(() => {
    if (storages) {
      console.log("Storages loaded:", storages);
    }
  }, [storages]);

  // Update storageSchedules when needsReviewData changes
  useEffect(() => {
    if (Object.keys(needsReviewData.schedules).length > 0) {
      const editable = buildEditableSchedules(needsReviewData.schedules);
      setStorageSchedules(editable);
      setOriginalSchedules(cloneSchedules(editable));
      setOriginalPayloads(buildPayloadMap(editable));
      setIsEditing(false);
    } else {
      setStorageSchedules({});
      setOriginalSchedules({});
      setOriginalPayloads({});
      setIsEditing(false);
    }
  }, [needsReviewData]);

  // Filter locations to only those that need review
  const locationsNeedingReview = locations.filter((loc) =>
    needsReviewData.storageIds.includes(loc.id),
  );

  // Keep current location in range when the list shrinks after updates
  useEffect(() => {
    if (
      locationsNeedingReview.length > 0 &&
      currentLocation >= locationsNeedingReview.length
    ) {
      setCurrentLocation(0);
    }
  }, [locationsNeedingReview.length, currentLocation]);

  // Get current schedule based on selected location (using storage id)
  const schedule =
    locationsNeedingReview.length > 0 &&
    locationsNeedingReview[currentLocation] &&
    storageSchedules[locationsNeedingReview[currentLocation].id]
      ? storageSchedules[locationsNeedingReview[currentLocation].id]
      : [];

  const currentStorageId =
    locationsNeedingReview[currentLocation]?.id ?? undefined;

  const currentSchedule = useMemo(() => {
    if (!currentStorageId) return [];
    return storageSchedules[currentStorageId] || [];
  }, [currentStorageId, storageSchedules]);

  const isCurrentScheduleValid = useMemo(
    () => validateSchedule(currentSchedule),
    [currentSchedule],
  );

  const hasChanges = useMemo(() => {
    if (!currentStorageId) return false;
    const baseline = originalPayloads[currentStorageId] || {};
    const currentPayload = buildTimesPayload(currentSchedule);
    return JSON.stringify(baseline) !== JSON.stringify(currentPayload);
  }, [currentSchedule, currentStorageId, originalPayloads]);

  const dayValidationErrors = useMemo(() => {
    const errors = {};
    currentSchedule.forEach((day, idx) => {
      const res = validateDay(day);
      errors[idx] = res.error;
    });
    return errors;
  }, [currentSchedule]);

  // Notify React Native WebView host that the modal has finished
  const notifyNativeClose = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: "CLOSE_MODAL" }));
    } else {
      const prefix = language && language !== "en" ? `/${language}` : "";
      window.location.href = `${prefix}/angel/dashboard`;
    }
  };

  const handleStatusSubmit = (status, timesPayload = null) => {
    if (!locationsNeedingReview[currentLocation]) return;

    const storageId = locationsNeedingReview[currentLocation].id;

    // Submit the status
    dispatch(actions.submitOpeningHourStatus(storageId, status, timesPayload));

    // Move to next location or close modal
    if (currentLocation < locationsNeedingReview.length - 1) {
      // Move to next location
      setCurrentLocation((prev) => prev + 1);
      setIsConfirmed(false);
      setIsEditing(false);
    } else {
      // No more locations, close modal
      notifyNativeClose();
      if (!standalone) {
        setIsOpen(false);
      }
      setCurrentLocation(0);
      setIsConfirmed(false);
      setIsEditing(false);
    }
  };

  const handleRequestChange = () => {
    setIsConfirmed(false);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (!currentStorageId) return;
    setStorageSchedules((prev) => ({
      ...prev,
      [currentStorageId]: cloneSchedules(
        originalSchedules[currentStorageId] || currentSchedule,
      ),
    }));
    setIsEditing(false);
  };

  const handleSubmitEdit = () => {
    if (!currentStorageId) return;
    const payload = buildTimesPayload(currentSchedule);
    setOriginalPayloads((prev) => ({
      ...prev,
      [currentStorageId]: payload,
    }));
    setOriginalSchedules((prev) => ({
      ...prev,
      [currentStorageId]: cloneSchedules(currentSchedule),
    }));
    handleStatusSubmit("CHANGE_REQUEST", payload);
  };

  const toggleDayClosed = (dayIndex) => {
    if (!currentStorageId) return;
    setStorageSchedules((prev) => {
      const next = { ...prev };
      const scheduleForStorage = [...(next[currentStorageId] || [])];
      const day = { ...(scheduleForStorage[dayIndex] || {}) };
      day.isClosed = !day.isClosed;
      day.slots = ensureTwoSlots(day.slots || []);
      scheduleForStorage[dayIndex] = day;
      next[currentStorageId] = scheduleForStorage;
      return next;
    });
  };

  const addSecondSlot = (dayIndex) => {
    if (!currentStorageId) return;
    setStorageSchedules((prev) => {
      const next = { ...prev };
      const scheduleForStorage = [...(next[currentStorageId] || [])];
      const day = { ...(scheduleForStorage[dayIndex] || {}) };
      day.slots = ensureTwoSlots(day.slots || []);
      day.showSecondSlot = true;
      scheduleForStorage[dayIndex] = day;
      next[currentStorageId] = scheduleForStorage;
      return next;
    });
  };

  const removeSecondSlot = (dayIndex) => {
    if (!currentStorageId) return;
    setStorageSchedules((prev) => {
      const next = { ...prev };
      const scheduleForStorage = [...(next[currentStorageId] || [])];
      const day = { ...(scheduleForStorage[dayIndex] || {}) };
      const slots = ensureTwoSlots(day.slots || []);
      slots[1] = { open: "", close: "" };
      day.slots = slots;
      day.showSecondSlot = false;
      scheduleForStorage[dayIndex] = day;
      next[currentStorageId] = scheduleForStorage;
      return next;
    });
  };

  const handleTimeChange = (dayIndex, slotIndex, field, value) => {
    if (!currentStorageId) return;

    setStorageSchedules((prev) => {
      const next = { ...prev };
      const scheduleForStorage = [...(next[currentStorageId] || [])];
      const day = { ...(scheduleForStorage[dayIndex] || {}) };
      const slots = ensureTwoSlots(day.slots || []);
      const slot = {
        ...slots[slotIndex],
        [field]: clampToHalfHour(normalizeTimeValue(value)),
      };
      slots[slotIndex] = slot;
      day.slots = slots;
      // If the user starts adding times, the day should not stay closed
      if (slot.open || slot.close) {
        day.isClosed = false;
      }
      scheduleForStorage[dayIndex] = day;
      next[currentStorageId] = scheduleForStorage;
      return next;
    });
  };

  const nextLocation = () => {
    setCurrentLocation((prev) => (prev + 1) % locationsNeedingReview.length);
  };

  const getDayName = (dayKey) => {
    return t(
      `openingHours_days_${dayKey}`,
      dayKey[0].toUpperCase() + dayKey.slice(1),
    );
  };

  const formatHours = (item) => {
    if (item.isClosed) return t("openingHours_closed", "Closed");
    const completeSlots = (item.slots || []).filter(
      (slot) => slot.open && slot.close,
    );
    if (completeSlots.length === 0) {
      return t("openingHours_closed", "Closed");
    }
    return completeSlots
      .map(
        (slot) =>
          `${formatTimeLabel(slot.open, uses12HourFormat)} - ${formatTimeLabel(slot.close, uses12HourFormat)}`,
      )
      .join("    ");
  };

  // Check if data is still loading
  const isLoading = !openingHours || !storages;

  // Angel data failed to load (e.g. non-angel token / invalid session).
  // On the standalone page there is no other chrome, so show an explicit
  // message instead of a blank white screen.
  const hasError = !!openingHoursError || !!storagesError;

  if (standalone && hasError) {
    return (
      <div
        className="opening-hours-token-error"
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          minHeight: "70vh",
          padding: "24px",
          gap: "8px",
        }}
      >
        <h2 style={{ margin: 0 }}>
          {t("openingHours_AuthIssueTitle", "Authentication issue")}
        </h2>
        <p style={{ margin: 0, maxWidth: "420px", opacity: 0.7 }}>
          {t(
            "openingHours_AuthIssue",
            "Please re install the app and reset your password to solve the issue.",
          )}
        </p>
      </div>
    );
  }

  return (
    <Modal
      isOpen={needsReviewData.storageIds.length > 0 && (standalone || isOpen)}
      className="modal-lg"
      dialogClassName="opening-hours-modal"
      bodyClassName="opening-hours-modal"
      closeBtn={false}
      centered
    >
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Loading />
        </div>
      ) : (
        <div className="card">
          <datalist id="halfHourOptions">
            {halfHourOptions.map((val) => (
              <option key={val} value={val} />
            ))}
          </datalist>
          <button
            className="fullscreen-btn"
            onClick={() => handleStatusSubmit("SKIPPED")}
          >
            <svg
              className="fullscreen-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          <div className="header">
            <h1 className="title">
              {t("openingHours_title", "Confirm your permanent opening hours")}
            </h1>
            <p className="subtitle">
              {t(
                "openingHours_subtitle",
                "Accurate opening hours prevent bookings outside your working time",
              )}
            </p>
          </div>

          {locationsNeedingReview.length > 0 && (
            <div className="location-info">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  {locationsNeedingReview.length > 1 && <p className="location-label">
                    {t(
                      "openingHours_locationCounter",
                      "Location {{current}} of {{total}}",
                      {
                        current: currentLocation + 1,
                        total: locationsNeedingReview.length,
                      },
                    )}
                  </p>}
                  <h2 className="location-name">
                    {t(
                      `openingHours_locations_${locationsNeedingReview[currentLocation]?.id}`,
                      {
                        defaultValue:
                          locationsNeedingReview[currentLocation]?.name || "",
                      },
                    )}
                  </h2>
                </div>
                {locationsNeedingReview.length > 1 && (
                  <button
                    style={{ display: "none" }}
                    className="caret-btn"
                    onClick={nextLocation}
                  >
                    <svg
                      aria-hidden="true"
                      className="fullscreen-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="m9 5 7 7-7 7"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="schedule-list">
            {schedule.length > 0 &&
              schedule.map((item, index) => (
                <div key={index} className="compact-row">
                  {isEditing ? (
                    <EditableDaySchedule
                      item={item}
                      index={index}
                      t={t}
                      dayLabel={getDayName(item.dayKey)}
                      uses12HourFormat={uses12HourFormat}
                      timeInputLocale={timeInputLocale}
                      toggleDayClosed={toggleDayClosed}
                      handleTimeChange={handleTimeChange}
                      onAddSecondSlot={addSecondSlot}
                      onRemoveSecondSlot={removeSecondSlot}
                      halfHourOptions={halfHourOptions}
                      closeOptions={closeHalfHourOptions}
                      validationError={dayValidationErrors[index]}
                    />
                  ) : (
                    <div
                      className={`schedule-item ${
                        isEditing || editHours ? "editable" : ""
                      }`}
                    >
                      <span className="schedule-day">
                        {getDayName(item.dayKey)}
                      </span>
                      <div className="schedule-hours-wrapper">
                        <span
                          className={`schedule-hours ${
                            item.isClosed ? "closed" : ""
                          }`}
                        >
                          {formatHours(item)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>

          {!isEditing && (
            <div className="confirmation">
              <label className="checkbox-label">
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    className="checkbox-input"
                    checked={isConfirmed}
                    onChange={(e) => {
                      setIsConfirmed(e.target.checked);
                    }}
                  />
                </div>
                <span className="checkbox-text">
                  {t(
                    "openingHours_confirmationText",
                    "I confirm that I reviewed my permanent opening hours and they are correct.",
                  )}
                </span>
              </label>
            </div>
          )}

          <div className="actions">
            {isEditing ? (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancelEdit}
                >
                  {t("openingHours_cancel", "Cancel")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!isCurrentScheduleValid || !hasChanges}
                  onClick={handleSubmitEdit}
                >
                  {t("openingHours_submitChanges", "Submit")}
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={handleRequestChange}
                >
                  {t("openingHours_requestChange", "Request a change")}
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!isConfirmed}
                  onClick={() => handleStatusSubmit("CONFIRMED")}
                >
                  {t("Confirm", "Confirm")}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default OpeningHoursConfirmationModal;
